import type { ReactNode } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

interface MobileFilterSheetShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The control that opens the sheet (icon button, pill, etc.). */
  trigger: ReactNode;
  /** Total number of active filters. Drives the Clear pill. */
  activeFilterCount: number;
  /** Called when the user taps "Clear all". */
  onClearFilters: () => void;
  children: ReactNode;
  /** A11y title (sr-only, never visible). Defaults to "Filters". */
  title?: string;
  /** Override max height in `svh` units. Defaults to 80 so the grab handle clears mobile browser chrome. */
  maxHeightVh?: number;
}

/**
 * Shared shell for every mobile filter tray.
 *
 * Mandates:
 * - Bottom sheet (auto grab handle + swipe-to-dismiss via SheetContent)
 * - sr-only title — no visible "Filters" header
 * - Right-aligned Clear pill with inline active-count chip (no layout shift)
 * - Live apply — never add a sticky Apply/Reset footer
 * - `svh` cap (NOT `vh`) so iOS URL bar can never eat the grab handle
 *
 * See mem://style/mobile-filter-tray-shell-v1
 */
export function MobileFilterSheetShell({
  open,
  onOpenChange,
  trigger,
  activeFilterCount,
  onClearFilters,
  children,
  title = 'Filters',
  maxHeightVh = 80,
}: MobileFilterSheetShellProps) {
  const hasActive = activeFilterCount > 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent
        side="bottom"
        className="bg-chrome border-white/[0.08] !pt-3 !gap-0 flex flex-col"
        style={{
          // `svh` = small viewport height — measured with mobile browser chrome
          // ALWAYS visible, so the grab handle can never hide behind Safari's URL bar.
          maxHeight: `${maxHeightVh}svh`,
          paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)',
        }}
      >
        <SheetHeader className="sr-only">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>

        {/* Fixed-height action row — Clear pill only */}
        <div className="flex h-9 items-center justify-end gap-2 px-1 shrink-0">
          <button
            type="button"
            onClick={onClearFilters}
            disabled={!hasActive}
            aria-hidden={!hasActive}
            tabIndex={hasActive ? 0 : -1}
            className={cn(
              'inline-flex h-9 items-center justify-center rounded-full px-3 text-sm font-medium transition-opacity',
              'text-active-gold hover:text-active-gold/80',
              'disabled:opacity-0 disabled:pointer-events-none',
            )}
          >
            Clear all
          </button>
        </div>

        <div className="flex flex-col gap-6 pt-2 overflow-y-auto -mx-2 px-2">{children}</div>
      </SheetContent>
    </Sheet>
  );
}
